import React, { useState, useCallback, useEffect, useMemo } from "react";
import { ChatContext } from "../contexts/ChatContext";
import { chatbotAPI } from "../models";

/**
 * Chat Provider
 * Manages global chat state and ChatGPT interactions
 *
 * @example
 * <ChatProvider>
 *   <App />
 * </ChatProvider>
 */
export const ChatProvider = ({ children }) => {
  // Initial welcome message
  const initialMessages = [
    {
      type: "bot",
      content:
        "👋 Hi! I'm your Koflip AI Assistant powered by ChatGPT. I can help you understand how Koflip works for homeowners and investors, explain our renovation-to-sale process, investment returns, and answer any questions you have. What would you like to know?",
      timestamp: new Date(),
      suggestions: [
        "What is Koflip?",
        "How do returns work?",
        "Try the Calculator",
      ],
    },
  ];

  // Chat state
  const [messages, setMessages] = useState(() => {
    // Try to load from localStorage
    try {
      const saved = localStorage.getItem("koflip_chat_messages");
      return saved ? JSON.parse(saved) : initialMessages;
    } catch {
      return initialMessages;
    }
  });

  const [isTyping, setIsTyping] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Thread ID for Assistants API (replaces conversationHistory)
  const [threadId, setThreadId] = useState(() => {
    return localStorage.getItem("koflip_thread_id") || null;
  });

  // Chat widget state
  const [isOpen, setIsOpen] = useState(false);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem("koflip_chat_messages", JSON.stringify(messages));
    } catch (err) {
      console.error("Failed to save messages to localStorage:", err);
    }
  }, [messages]);

  // Save thread ID to localStorage
  useEffect(() => {
    if (threadId) {
      localStorage.setItem("koflip_thread_id", threadId);
    } else {
      localStorage.removeItem("koflip_thread_id");
    }
  }, [threadId]);

  // Initialize ChatGPT API
  useEffect(() => {
    const initializeChatGPT = async () => {
      try {
        const response = await chatbotAPI.checkHealth();

        if (response.success && response.data?.available) {
          setIsAvailable(true);
          console.log("✅ ChatGPT AI Assistant ready", response.data);
        } else {
          setIsAvailable(false);
          setError(
            "AI service is currently unavailable. Please try again later."
          );
          console.warn("⚠️ ChatGPT service not available");
        }
      } catch (err) {
        console.error("❌ Failed to initialize ChatGPT:", err);
        setError("Failed to connect to AI service");
        setIsAvailable(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeChatGPT();
  }, []);

  // Add message to chat
  const addMessage = useCallback((message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  // Clear all messages
  const clearMessages = useCallback(() => {
    setMessages(initialMessages);
    setThreadId(null); // Clear thread to start fresh
  }, []);

  // Start new conversation (creates new thread)
  const startNewConversation = useCallback(() => {
    setThreadId(null); // Clear thread ID - new one will be created on next message
    clearMessages();
  }, [clearMessages]);

  // Send message to ChatGPT (Assistants API with threads)
  const sendMessage = useCallback(
    async (messageText) => {
      if (!messageText.trim()) return;

      // Check if service is available
      if (!isAvailable) {
        const errorMsg = {
          type: "bot",
          content:
            error ||
            "AI service is currently unavailable. Please try again later.",
          timestamp: new Date(),
          isError: true,
        };
        addMessage(errorMsg);
        return;
      }

      // Add user message
      const userMessage = {
        type: "user",
        content: messageText,
        timestamp: new Date(),
      };
      addMessage(userMessage);

      // Set typing indicator
      setIsTyping(true);

      try {
        // Call ChatGPT Assistants API with thread ID
        const response = await chatbotAPI.sendMessage(messageText, threadId);

        if (!response.success) {
          throw new Error(response.error || "Failed to get response");
        }

        // Store thread ID for next message (OpenAI manages conversation context)
        if (response.data.threadId) {
          setThreadId(response.data.threadId);
        }

        // Add bot response
        const botResponse = {
          type: "bot",
          content: response.data.response,
          timestamp: new Date(),
          suggestions: response.data.suggestions || [],
        };

        addMessage(botResponse);

        console.log(
          `💬 Thread ${response.data.isNewThread ? "created" : "continued"}: ${
            response.data.threadId
          }`
        );
      } catch (err) {
        console.error("❌ ChatGPT error:", err);

        // Show error message
        const errorResponse = {
          type: "bot",
          content:
            err.message ||
            "I apologize, but I'm having trouble processing your request right now. Please try again in a moment.",
          timestamp: new Date(),
          isError: true,
          suggestions: [
            "What is Koflip?",
            "How does it work?",
            "Try the Calculator",
          ],
        };

        addMessage(errorResponse);
      } finally {
        setIsTyping(false);
      }
    },
    [isAvailable, error, addMessage, threadId]
  );

  // Chat widget controls
  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);
  const toggleChat = useCallback(() => setIsOpen((prev) => !prev), []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      // Messages
      messages,
      addMessage,
      clearMessages,

      // Chat state
      isTyping,
      setIsTyping,

      // ChatGPT availability
      isAvailable,
      isLoading,
      error,

      // Send message
      sendMessage,

      // Conversation / Thread
      threadId,
      conversationId: threadId, // For backward compatibility
      startNewConversation,

      // Chat widget
      isOpen,
      openChat,
      closeChat,
      toggleChat,
    }),
    [
      messages,
      addMessage,
      clearMessages,
      isTyping,
      isAvailable,
      isLoading,
      error,
      sendMessage,
      threadId,
      startNewConversation,
      isOpen,
      openChat,
      closeChat,
      toggleChat,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
